import { useState } from 'react';
import type { T } from '../../../messages';
import type { CatalogItem } from '../../../wire';

/**
 * The small facts every part of the catalog tab shares: one language of a
 * stored map, money as a box holds it and as a list prints it, and which
 * kind-tab an item belongs under.
 */

/**
 * One language of a stored map, for a text box.
 *
 * The console edits the **tenant's default locale** and leaves every other key
 * where it is. Sending a bare string would replace the whole map
 * (`updateService`'s rule — "a patch replaces the map rather than merging into
 * it"), so an operator fixing an English typo would silently drop the Spanish.
 * Sending a map with the other keys preserved is the same edit without that.
 */
export function textOf(map: Record<string, string> | null, locale: string): string {
  if (map === null) return '';
  return map[locale] ?? Object.values(map)[0] ?? '';
}

export function withLocale(
  map: Record<string, string> | null,
  locale: string,
  value: string,
): Record<string, string> {
  return { ...(map ?? {}), [locale]: value };
}

/** Cents as a box holds them: `125.00`, no symbol, so what is typed is what is read. */
export function money(cents: number): string {
  return (cents / 100).toFixed(2);
}

/**
 * Cents as a list prints them: `$125.00`, in the business's currency and the
 * operator's language. A code `Intl` does not know falls back to the box's
 * spelling rather than throwing on a whole list.
 */
export function priceText(cents: number, currency: string, uiLocale: string): string {
  try {
    return new Intl.NumberFormat(uiLocale, { style: 'currency', currency }).format(cents / 100);
  } catch {
    return money(cents);
  }
}

/**
 * The other language of the fleet's pair. Two, not N: the guest site and the
 * staff app are catalogued in English and Spanish and nothing else, so the
 * length editor's second box is one box rather than a language picker.
 */
export function secondLocaleOf(locale: string): 'en' | 'es' {
  return locale.toLowerCase().startsWith('es') ? 'en' : 'es';
}

/**
 * One tab per kind the business sells, drawn only when it sells one of it.
 * `stay` and `departure` are **read-only** (`docs/verticals-plan.md` V4): a
 * room type is a calendar and a fee list and a policy, and the file that states
 * it is the door. `other` is whatever kind arrives after these.
 */
export const KINDS = ['scheduled', 'stocked', 'prepared', 'stay', 'departure', 'other'] as const;
export type Kind = (typeof KINDS)[number];

export function kindOf(kind: string): Kind {
  return (KINDS as readonly string[]).includes(kind) && kind !== 'other' ? (kind as Kind) : 'other';
}

export function kindLabel(t: T, kind: Kind): string {
  switch (kind) {
    case 'scheduled':
      return t('catalog.tab.scheduled');
    case 'stocked':
      return t('catalog.tab.stocked');
    case 'prepared':
      return t('catalog.tab.prepared');
    case 'stay':
      return t('catalog.tab.stay');
    case 'departure':
      return t('catalog.tab.departure');
    default:
      return t('catalog.tab.other');
  }
}

/** A room type or a trip: drawn, never edited here. */
export function isReadOnlyKind(kind: Kind): boolean {
  return kind === 'stay' || kind === 'departure';
}

/** An item's heading, from whichever of the two columns its kind uses (0024 decision 1). */
export function headingMap(item: CatalogItem): Record<string, string> | null {
  return item.menu === null ? item.category : item.menu.category;
}

/**
 * **Repaint from the server's answer, not from optimism.**
 *
 * Every length and add-on write answers with the item as it now *is*, because
 * these writes move numbers the caller did not name: marking a new default
 * restates the item's headline price and minutes in the same transaction. A
 * block that redrew itself from what it sent would show an operator a price
 * the catalog no longer holds, for as long as the reload takes.
 *
 * So the answer's item is held here until the tab's own re-read lands — and the
 * reset is the "adjust state when a prop changes" shape rather than an effect,
 * so there is never a frame showing the stale one.
 */
export function useFreshItem(item: CatalogItem): [CatalogItem, (next: CatalogItem) => void] {
  const [fresh, setFresh] = useState<CatalogItem | null>(null);
  const [basis, setBasis] = useState(item);
  if (basis !== item) {
    setBasis(item);
    setFresh(null);
  }
  return [fresh ?? item, setFresh];
}
